/**
 * EmailPerformance.js — Email tab
 * "Are my emails working?"
 * Pulls from /api/analytics/email-performance + /api/analytics/email-revenue
 */
import React, { useState, useEffect, useCallback } from 'react';
import { get } from '../api';

const RANGES = [
  { label: '7D', value: '7d' },
  { label: '30D', value: '30d' },
  { label: '90D', value: '90d' },
];

function StatCard({ label, value, sub, title }) {
  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12,
      padding: '18px 20px', minWidth: 0,
    }} title={title}>
      <div style={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--muted)', marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em' }}>{value ?? '—'}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: "'JetBrains Mono', monospace", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function Metric({ label, value, color, title }) {
  return (
    <div title={title} style={{ minWidth: 70 }}>
      <div style={{
        fontSize: 9,
        fontFamily: "'JetBrains Mono', monospace",
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: 'var(--muted)',
        marginBottom: 3,
        whiteSpace: 'nowrap',
      }}>{label}</div>
      <div style={{
        fontSize: 13,
        fontFamily: "'JetBrains Mono', monospace",
        fontWeight: 800,
        color: color || 'var(--text)',
        whiteSpace: 'nowrap',
      }}>{value ?? '—'}</div>
    </div>
  );
}

const fmtPct = v => v != null ? `${Number(v).toFixed(2)}%` : '—';
const fmtDollar = v => v != null ? `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';
const fmtNum = v => v != null ? Number(v).toLocaleString() : '—';
const fmtDate = iso => {
  if (!iso) return '—';
  const m = String(iso).match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '—';
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[parseInt(m[2], 10) - 1]} ${parseInt(m[3], 10)}`;
};
const fmtRatioPct = v => {
  if (v == null || v === undefined) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${(n <= 1 ? n * 100 : n).toFixed(1)}%`;
};
const ratioAtLeast = (v, threshold) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return false;
  return (n <= 1 ? n : n / 100) >= threshold;
};
const fmtSegment = value => {
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'all';
  return value || 'all';
};
const ctrTooltip = 'Unique recipients who clicked a tracked content link. Excludes footer, unsubscribe, and corporate email scanner traffic.';
const rawClickSub = item => item?.raw_clicks
  ? `Raw clicks: ${fmtNum(item.raw_clicks)} · ${Number(item.bot_click_filter_pct || 0).toFixed(1)}% bots filtered`
  : null;

export default function EmailPerformance() {
  const [range, setRange] = useState('30d');
  const [perf, setPerf] = useState(null);
  const [rev, setRev] = useState(null);
  const [recentEmails, setRecentEmails] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      get(`/api/analytics/email-performance?range=${range}`).catch(() => null),
      get(`/api/analytics/email-revenue?range=${range}`).catch(() => null),
      get('/api/analytics/recent-emails?limit=10').catch(() => null),
    ]).then(([p, r, recent]) => {
      setPerf(p);
      setRev(r);
      setRecentEmails(recent?.emails || []);
      setLoading(false);
    });
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const b = perf?.broadcast || {};
  const t = perf?.triggered || {};
  const s = rev?.summary || {};
  const campaigns = rev?.campaigns || [];
  const topEmails = perf?.top_emails || [];

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 2 }}>Email Performance</h2>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Are my emails working?</div>
        </div>
        <div style={{ display: 'flex', background: 'var(--surface)', borderRadius: 10, border: '1px solid var(--border)', overflow: 'hidden' }}>
          {RANGES.map(r => (
            <button key={r.value} onClick={() => setRange(r.value)} style={{
              padding: '8px 16px', fontSize: 12, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600,
              border: 'none', cursor: 'pointer',
              background: range === r.value ? 'var(--blue)' : 'transparent',
              color: range === r.value ? '#fff' : 'var(--muted)',
            }}>{r.label}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--muted)' }}>Loading...</div>
      ) : (
        <>
          {/* Recent emails sent — moved from Command Center */}
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 20, marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--muted)', marginBottom: 4 }}>
                  Recent Emails Sent
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>Broadcast sends with linked trigger follow-up performance.</div>
              </div>
            </div>
            {recentEmails.length === 0 ? (
              <div style={{ color: 'var(--muted)', fontSize: 13 }}>No sent emails yet</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, overflowX: 'auto', paddingBottom: 2 }}>
                {recentEmails.map((email, i) => (
                  <div key={email.id || i}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 1fr) repeat(6, minmax(70px, auto))', gap: 10, alignItems: 'center', minWidth: 760, padding: '10px 12px', background: 'var(--bg)', borderRadius: email.trigger ? '10px 10px 0 0' : 10, borderBottom: email.trigger ? '1px solid var(--border)' : 'none' }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email.subject}</div>
                        <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: "'JetBrains Mono', monospace", marginTop: 2 }}>
                          {fmtSegment(email.segment_target)} · broadcast · {fmtDate(email.sent_at)}
                        </div>
                      </div>
                      <Metric label="Sent" value={fmtNum(email.recipients)} />
                      <Metric label="Opens" value={fmtNum(email.opens)} color="#3B82F6" />
                      <Metric label="Open %" value={fmtRatioPct(email.open_rate)} color={ratioAtLeast(email.open_rate, 0.2) ? 'var(--green)' : undefined} />
                      <Metric label="Clicks" value={fmtNum(email.cta_clicks ?? email.clicks)} color="#10B981" title={ctrTooltip} />
                      <Metric label="Click %" value={fmtRatioPct(email.click_rate)} color={ratioAtLeast(email.click_rate, 0.02) ? 'var(--green)' : 'var(--amber)'} title={ctrTooltip} />
                      <Metric label="Revenue" value={fmtDollar(email.revenue)} color="var(--green)" />
                    </div>
                    {email.trigger && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 1fr) repeat(6, minmax(70px, auto))', gap: 10, alignItems: 'center', minWidth: 760, padding: '8px 12px 8px 24px', background: 'rgba(245,158,11,0.06)', borderRadius: '0 0 10px 10px', borderTop: '1px solid rgba(245,158,11,0.15)' }}>
                        <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ color: '#F59E0B', fontSize: 13, flexShrink: 0 }}>↳</span>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#cbd5e1' }}>{email.trigger.subject}</div>
                            <div style={{ fontSize: 10, color: '#F59E0B', fontFamily: "'JetBrains Mono', monospace", marginTop: 2, opacity: 0.85 }}>trigger · fires on click</div>
                          </div>
                        </div>
                        <Metric label="Fired" value={fmtNum(email.trigger.recipients)} />
                        <Metric label="Opens" value={fmtNum(email.trigger.opens)} color="#3B82F6" />
                        <Metric label="Open %" value={fmtRatioPct(email.trigger.open_rate)} color={ratioAtLeast(email.trigger.open_rate, 0.2) ? 'var(--green)' : undefined} />
                        <Metric label="Clicks" value={fmtNum(email.trigger.clicks)} color="#10B981" />
                        <Metric label="Click %" value={fmtRatioPct(email.trigger.click_rate)} color={ratioAtLeast(email.trigger.click_rate, 0.02) ? 'var(--green)' : 'var(--amber)'} />
                        <Metric label="Revenue" value={fmtDollar(email.trigger.revenue)} color="var(--green)" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Broadcast vs Trigger Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
            {/* Broadcast */}
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--blue)' }} />
                <span style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--muted)' }}>
                  Broadcast ({b.email_count || 0} emails)
                </span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <StatCard label="Sends" value={fmtNum(b.sends)} />
                <StatCard label="Open Rate" value={fmtPct(b.open_rate)} />
                <StatCard label="CTR" value={fmtPct(b.click_rate)} sub={rawClickSub(b) || (b.click_rate >= 2 ? '✅ Good' : '⚠️ Below avg')} title={ctrTooltip} />
                <StatCard label="Unsub Rate" value={fmtPct(b.unsub_rate)} />
              </div>
            </div>

            {/* Triggered */}
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--green)' }} />
                <span style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--muted)' }}>
                  Triggered ({t.email_count || 0} sequences)
                </span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <StatCard label="Sends" value={fmtNum(t.sends)} />
                <StatCard label="Open Rate" value={fmtPct(t.open_rate)} />
                <StatCard label="CTR" value={fmtPct(t.click_rate)} title={ctrTooltip} />
                <StatCard label="Revenue" value={fmtDollar(t.revenue)} />
              </div>
            </div>
          </div>

          {/* Revenue Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
            <StatCard label="Attributed Revenue" value={fmtDollar(s.attributed_revenue)} sub="Email-driven" />
            <StatCard label="Broadcast Rev" value={fmtDollar(s.broadcast_revenue)} />
            <StatCard label="Trigger Rev" value={fmtDollar(s.trigger_revenue)} />
            <StatCard label="Rev / Send" value={fmtDollar(s.rev_per_send)} />
            <StatCard label="Rev / Click" value={fmtDollar(s.rev_per_click)} />
            <StatCard label="Rev / Subscriber" value={fmtDollar(s.rev_per_subscriber)} />
          </div>

          {/* Top emails by click rate */}
          {topEmails.length > 0 && (
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 20, marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--muted)', marginBottom: 14 }}>
                Top 5 Emails by CTR
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Subject', 'Segment', 'Sends', 'Open%', 'CTR', 'Revenue'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontSize: 10, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {topEmails.map(e => (
                    <tr key={e.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '10px 12px', fontWeight: 500, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.subject}</td>
                      <td style={{ padding: '10px 12px', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, textTransform: 'capitalize' }}>{e.segment}</td>
                      <td style={{ padding: '10px 12px', fontFamily: "'JetBrains Mono', monospace" }}>{fmtNum(e.sends)}</td>
                      <td style={{ padding: '10px 12px', fontFamily: "'JetBrains Mono', monospace", color: e.open_rate >= 20 ? 'var(--green)' : 'inherit' }}>{fmtPct(e.open_rate)}</td>
                      <td style={{ padding: '10px 12px', fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: e.click_rate >= 2 ? 'var(--green)' : 'var(--amber)' }} title={ctrTooltip}>
                        {fmtPct(e.click_rate)}
                        {e.raw_clicks ? <div style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 400 }}>Raw: {fmtNum(e.raw_clicks)} · {Number(e.bot_click_filter_pct || 0).toFixed(1)}% bots</div> : null}
                      </td>
                      <td style={{ padding: '10px 12px', fontFamily: "'JetBrains Mono', monospace", color: 'var(--green)' }}>{fmtDollar(e.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* All campaigns table */}
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 20, marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--muted)', marginBottom: 14 }}>
              All Campaigns ({range.toUpperCase()})
            </div>
            {campaigns.length === 0 ? (
              <div style={{ color: 'var(--muted)', fontSize: 13 }}>No campaigns in this period</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      {['Subject', 'Segment', 'Type', 'Sends', 'Open%', 'CTR', 'Revenue', 'Rev/Send'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontSize: 10, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.map(e => (
                      <tr key={e.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '10px 12px', fontWeight: 500, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.subject}</td>
                        <td style={{ padding: '10px 12px', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, textTransform: 'capitalize' }}>{e.segment}</td>
                        <td style={{ padding: '10px 12px', fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>{e.type}</td>
                        <td style={{ padding: '10px 12px', fontFamily: "'JetBrains Mono', monospace" }}>{fmtNum(e.sends)}</td>
                        <td style={{ padding: '10px 12px', fontFamily: "'JetBrains Mono', monospace" }}>{fmtPct(e.open_rate)}</td>
                        <td style={{ padding: '10px 12px', fontFamily: "'JetBrains Mono', monospace", color: e.click_rate >= 2 ? 'var(--green)' : 'inherit' }} title={ctrTooltip}>
                          {fmtPct(e.click_rate)}
                          {e.raw_clicks ? <div style={{ fontSize: 10, color: 'var(--muted)' }}>Raw: {fmtNum(e.raw_clicks)} · {Number(e.bot_click_filter_pct || 0).toFixed(1)}% bots</div> : null}
                        </td>
                        <td style={{ padding: '10px 12px', fontFamily: "'JetBrains Mono', monospace", color: 'var(--green)' }}>{fmtDollar(e.revenue)}</td>
                        <td style={{ padding: '10px 12px', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: 'var(--muted)' }}>{fmtDollar(e.rev_per_send)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
